import { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { UTILS } from "utils";
import { API } from "api";
import { CONSTANTS as C } from "config";
import { errorHandler } from "api/api/errorHandler";
import AppModal from "components/ui/AppModal";
import BulkMenu from "features/bulk-menu/BulkMenu";
import AppContainer from "components/ui/AppContainer";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AppDropDown from "components/ui/AppDropdown";
import AppIcon from "components/ui/AppIcon";
import AppOutlineButton from "components/ui/AppOutlineButton";
import CreateItem from "features/menu/components/CreateItem";
import UpdateItem from "features/menu/components/UpdateItem";

const ItemContainer = ({
  onSaveAndContinue = () => {},
  as = "component",
  title = "Manage Items",
  subTitle = "Add and edit the products you sell. set names, prices, images and details to keep your store up yo date.",
}) => {
  const router = useHistory();
  const currentPage = router?.location?.state?.activePage ?? 1;
  const recordsPerPage = router?.location?.state?.pageSize ?? 10;
  const { syncData } = useSelector((state) => state.MenuReducer);
  const { integrations } = useSelector((state) => state.SettingsReducer);
  const [items, setItems] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    deleteItemIndex: null,
    item: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const [bulkMenuModal, setBulkMenuModal] = useState(false);
  const [searchTerm, setSearch] = useState("");
  const [showCreateItem, setShowCreateItem] = useState(false);
  const [showUpdateItem, setShowUpdateItem] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const isAiInfluencerEnabled = useMemo(() => {
    return (
      integrations?.some(
        (integration) => integration?.name === "CREATIFY_AI"
      ) || false
    );
  }, [integrations]);

  const renderTitleWithImage = (value, rowData, index) => {
    const videoExtensions = [
      ".mp4",
      ".m4v",
      ".webm",
      ".ogv",
      ".ogg",
      ".avi",
      ".mov",
      ".qt",
      ".wmv",
      ".flv",
      ".mkv",
      ".mk3d",
      ".mka",
    ];
    const file = rowData.itemImageURLs?.[rowData.itemImageURLs.length - 1 ?? 0];
    const isVideo = file
      ? videoExtensions.some((ext) => file.endsWith(ext))
      : false;
    return (
      <div className="flex justify-between items-center">
        {isVideo && file ? (
          <video
            src={file}
            muted
            autoPlay
            loop
            className="h-14 w-14 shrink-0 rounded-lg object-cover sm:h-20 sm:w-20"
          ></video>
        ) : (
          <img
            alt=""
            src={file ?? C.PLACEHOLDER_IMAGE}
            className="h-14 w-14 shrink-0 rounded-lg object-cover sm:h-20 sm:w-20"
          />
        )}
        <div className="start w-48 md:w-full ms-2 md:ms-4 my-auto">
          <h3 className="text-16px font-bold truncate-line-2 mb-0">
            {UTILS.isValidObject(["baseLanguage", "title"], rowData)
              ? rowData.baseLanguage.title
              : ""}
          </h3>
          <p className="text-12px text-gray-500 pt-1 truncate-line-4">
            {UTILS.isValidObject(["baseLanguage", "description"], rowData)
              ? rowData.baseLanguage.description
              : ""}
          </p>
        </div>
      </div>
    );
  };

  const renderDate = (value, rowData, index) => {
    return (
      <div className="px-3 py-1 whitespace-nowrap text-left">
        <h3>
          {rowData.createdAt ? new Date(rowData.createdAt).toDateString() : ""}
        </h3>
        <h5 className="leading-3 text-12px text-gray-500">
          {rowData.createdAt
            ? new Date(rowData.createdAt).toLocaleTimeString()
            : ""}
        </h5>
      </div>
    );
  };

  const renderActions = (value, rowData, index) => {
    const items = [
      {
        label: (
          <span className="flex items-center text-gray-600 gap-3 px-2 py-2">
            <AppIcon name="edit" />
            Edit Item
          </span>
        ),
        key: "edit",
      },
      {
        label: (
          <span className="flex items-center text-gray-600 gap-3 px-2 py-2">
            <AppIcon name="handStars" />
            AI Influencer
          </span>
        ),
        key: "aiinfluencer",
        disabled: true,
      },
      {
        label: (
          <span className="flex items-center text-gray-600 gap-3 px-2 py-2">
            <AppIcon name="trash" />
            Delete
          </span>
        ),
        key: "delete",
      },
    ];

    const dropDownClicked = (key, rowData) => {
      if (key == "edit") {
        updateItem(rowData._id);
      } else if (key == "delete") {
        onClickDeleteButton(rowData._id);
      } else {
        router.push("/app/dashboard/items/ai-influencer", {
          itemId: rowData._id,
          activePage: currentPage,
          pageSize: recordsPerPage,
          isAiInfluencerEnabled: isAiInfluencerEnabled,
        });
      }
    };
    return (
      <AppDropDown
        icon="more"
        dropdownMenu={items}
        onItemClick={(k) => dropDownClicked(k, rowData)}
      />
    );
  };

  const columns = [
    {
      key: "baseLanguage",
      title: t("Item"),
      dataIndex: "baseLanguage",
      align: "initial",
      render: renderTitleWithImage,
    },
    {
      key: "price",
      title: t("Price"),
      dataIndex: "price",
      align: "initial",
      sorter: {
        compare: (a, b) => a.price - b.price,
        multiple: 3,
      },
      render: (value, rowData, index) => {
        return <div>{t("AED") + " " + value}</div>;
      },
    },
    {
      key: "createdAt",
      title: t("Created At"),
      dataIndex: "createdAt",
      align: "initial",
      render: renderDate,
      sorter: {
        compare: (a, b) => a.math - b.math,
        multiple: 2,
      },
    },
    {
      title: t("Actions"),
      render: renderActions,
      align: "initial",
      fixed: "right",
    },
  ];

  const getItemsByBrand = async () => {
    setLoading(true);
    const userDetail = UTILS.getCurrentUser();
    const result = await API.GET_ITEMS_BY_BRAND({
      token: userDetail.token,
      brandId: userDetail.brandId,
      language: "EN",
    });
    if (result?.success === 1) setItems(result.data);
    else {
    }
    setLoading(false);
  };

  useEffect(() => {
    getItemsByBrand();
  }, [syncData]);

  const updateItem = (id) => {
    setSelectedItemId(id);
    setShowUpdateItem(true);
  };

  const createItem = () => {
    setShowCreateItem(true);
  };

  const onClickDeleteButton = (id) => {
    const index = items.findIndex((obj) => obj._id === id);
    if (index !== -1) {
      const item = items[index];
      setDeleteModal({ isOpen: true, deleteItemIndex: index, item: item });
    }
  };

  const handleCancel = () => {
    setDeleteModal({ isOpen: false, deleteItemIndex: -1, item: null });
  };
  const onDeleteItem = async () => {
    const item = items[deleteModal.deleteItemIndex];
    if (!item) {
      handleCancel();
      return;
    }
    try {
      setIsDeleting(true);
      const userDetail = UTILS.getCurrentUser();
      const response = await API.DELETE_ITEM({
        brandId: userDetail.brandId,
        itemId: item._id,
        token: userDetail.token,
      });
      if (response?.success === 1) {
        UTILS.handleSuccesNotification(response?.message ?? t("Item Deleted"));
        let arr = [...items];
        arr.splice(deleteModal.deleteItemIndex, 1);
        setItems(arr);
      } else {
        UTILS.handleErrorNotification(errorHandler(response?.message));
      }
    } catch (error) {
      UTILS.handleErrorNotification(errorHandler(error?.error?.message));
    } finally {
      setIsDeleting(false);
      handleCancel();
    }
  };

  const Items = useMemo(() => {
    if (searchTerm === "" || searchTerm === null) {
      return items;
    }
    return items.filter((item) =>
      item?.baseLanguage?.title?.toLowerCase().includes(searchTerm.trim())
    );
  }, [items, searchTerm]);

  const itemsTotalPages = Math.max(
    1,
    Math.ceil((Items?.length || 0) / recordsPerPage)
  );
  const effectivePage = Math.min(Math.max(currentPage, 1), itemsTotalPages);

  const handSearch = (key, val) => {
    router.push(router.location.pathname, {
      ...router?.location?.state,
      pageSize: recordsPerPage,
      activePage: 1,
    });
    setSearch(val.toLowerCase());
  };

  const onPaginationChange = (newPage, newPageSize) => {
    const nextPageSize = newPageSize ?? recordsPerPage;
    const obj = {};
    if (recordsPerPage !== nextPageSize) {
      obj.pageSize = nextPageSize;
    }
    if (currentPage !== newPage) {
      obj.activePage = newPage;
    }
    if (Object.keys(obj).length > 0) {
      router.push(router.location.pathname, {
        ...router?.location?.state,
        ...obj,
      });
    }
  };

  const footer = (
    <div className="flex justify-end">
      <AppButton onClick={() => onSaveAndContinue?.()}>
        {t("Save & Continue")}
      </AppButton>
    </div>
  );

  return showCreateItem ? (
    <CreateItem
      inline
      onCancel={() => setShowCreateItem(false)}
      onCreated={async () => {
        setShowCreateItem(false);
        await getItemsByBrand();
      }}
    />
  ) : showUpdateItem ? (
    <UpdateItem
      inline
      itemId={selectedItemId}
      onCancel={() => {
        setShowUpdateItem(false);
        setSelectedItemId(null);
      }}
      onUpdated={async () => {
        setShowUpdateItem(false);
        setSelectedItemId(null);
        await getItemsByBrand();
      }}
    />
  ) : (
    <AppContainer
      title={t(title)}
      subTitle={t(subTitle)}
      loading={loading}
      footer={
        as === "page" ? null : showCreateItem || showUpdateItem ? footer: footer
      }
      containerProps={{ className: "!md:overflow-y-hidden" }}
    >
      <AppModal
        open={deleteModal.isOpen}
        onClose={() => {
          if (isDeleting) return;
          handleCancel();
        }}
        width={550}
        closable={false}
        footer={
          <div className="mx-auto flex w-full max-w-[760px] items-center justify-center gap-4">
            <AppButton
              variant="secondary"
              size="lg"
              className=" w-1/2 border border-gray-400 bg-white text-4xl text-gray-500"
              onClick={handleCancel}
              disabled={isDeleting}
            >
              {t("No, Keep It")}
            </AppButton>
            <AppButton
              variant="primary"
              size="lg"
              className="w-1/2 text-4xl"
              onClick={onDeleteItem}
              loading={isDeleting}
            >
              {t("Yes, Delete")}
            </AppButton>
          </div>
        }
      >
        <div className="flex flex-col items-center px-8 text-center">
          <div className="mb-6 h-44 w-44 mt-6">
            <AppIcon name="trashRed" size={170} className="h-full w-full" />
          </div>
          <h1 className="text-24px font-semibold text-gray-900">
            {t("Delete Item")}
          </h1>
          <p className=" text-3xl text-gray-500 mb-0">
            {t("Are you sure you want to delete this item")}
          </p>
        </div>
      </AppModal>
      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex flex-row gap-2 justify-between items-center flex-wrap">
          <h2 className="text-24px font-semi-bold">{t("Products")}</h2>
          <div className="flex flex-row items-center flew-wrap justify-end gap-3">
            <div>
              <AppSearchInput
                placeholder={t("Search Item")}
                value={searchTerm}
                onInput={(e) => handSearch("searchTerm", e.target.value)}
              />
            </div>
            <div className="flex gap-3 justify-center items-center flex-wrap">
              <AppOutlineButton
                onClick={() => {
                  setBulkMenuModal(true);
                }}
              >
                {t("Import")}
              </AppOutlineButton>
              <AppButton onClick={() => createItem()}>{t("Create")}</AppButton>
              <BulkMenu open={bulkMenuModal} setOpen={setBulkMenuModal} />
            </div>
          </div>
        </div>
        <div className="flex min-h-0 md:flex-1 flex-col overflow-x-auto md:overflow-hidden">
          <AppPaginatedTable
            columns={columns}
            data={Items}
            clientSidePagination
            page={effectivePage}
            pageSize={recordsPerPage}
            onPageChange={onPaginationChange}
            rowOptions={[10, 20, 50, 100]}
          />
        </div>
      </div>
    </AppContainer>
  );
};

export default ItemContainer;
